import { useTranslation } from "@opendash/i18n";
import { Flex, InputNumber, Select } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";

enum TimeUnit {
  milliseconds = "milliseconds",
  seconds = "seconds",
  minutes = "minutes",
  hours = "hours",
  days = "days",
  weeks = "weeks",
  months = "months",
  years = "years",
}

export type TimespanFielType = {
  value: number;
  unit: TimeUnit;
};

type Props = {
  value: TimespanFielType;
  setValue: (value: TimespanFielType) => void;
};

/**
 * TimespanField is a component that allows the user to input a timespan.
 * This is done by using an input field that accepts a number and a unit of time (seconds, minutes, hours, days, weeks, months, years).
 * The component also provides a dropdown to select the unit of time and an input field to input a number.
 *
 * @param {TimespanFielType} value - The current value of the timespan.
 * @param {function} setValue - A function to set the value of the timespan.
 */
export default observer((props: Props) => {
  const t = useTranslation();
  const [state, setState] = useState<TimespanFielType>({
    value: props.value && props.value.value ? props.value.value : 0,
    unit:
      props.value && props.value.unit
        ? props.value.unit
        : TimeUnit.milliseconds,
  });

  useEffect(() => {
    if (props.setValue) {
      props.setValue(state);
    }
  }, [state.value, state.unit]);

  const valueText = `${props.value.value} ${t(`parse-admin:global.units.${props.value.unit}`)}`;

  return (
    <>
      {!props.setValue && (
        <span>
          {props.value.value && props.value.unit
            ? valueText
            : t("parse-admin:global.not_found")}
        </span>
      )}

      {props.setValue ? (
        <Flex align="stretch" justify="space-between" gap={8} vertical>
          <InputNumber
            min={0}
            defaultValue={props.value ? props.value.value : 0}
            onChange={(value) => {
              setState({ ...state, value: value as number });
            }}
            addonAfter={
              <Select
                style={{ width: 150 }}
                defaultValue={
                  props.value && props.value.unit
                    ? props.value.unit
                    : Object.keys(TimeUnit)[0]
                }
                onChange={(value) => {
                  setState({ ...state, unit: value as TimeUnit });
                }}
                options={[
                  {
                    label: t(
                      "parse-admin:global.units." + TimeUnit.milliseconds
                    ),
                    value: TimeUnit.milliseconds,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.seconds),
                    value: TimeUnit.seconds,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.minutes),
                    value: TimeUnit.minutes,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.hours),
                    value: TimeUnit.hours,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.days),
                    value: TimeUnit.days,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.weeks),
                    value: TimeUnit.weeks,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.months),
                    value: TimeUnit.months,
                  },
                  {
                    label: t("parse-admin:global.units." + TimeUnit.years),
                    value: TimeUnit.years,
                  },
                ]}
              />
            }
          />
        </Flex>
      ) : null}
    </>
  );
});
